import clsx from "clsx";
import { useCallback, useEffect, useRef, useState, RefObject } from "react";
import Bowser from "bowser";
import { AnimatePresence, motion } from "framer-motion";
import { AnimateChangeInHeight } from "@/components/animations/animateHeight";
import { CheckIcon, DevicePhoneMobileIcon } from "@heroicons/react/20/solid";
import { useMutation } from "@tanstack/react-query";
import {
	getCurrentTime,
	getUserIP,
	postZap,
	sendEmail,
	useSearchEngineDetector,
} from "@/lib/utils";
import {
	employment_status,
	loan_type,
	living_status,
	LOAN_TYPE_TRUCK,
	LOAN_TYPE_EQUIPMENT,
	LOAN_PURPOSE_PERSONAL,
	LOAN_PURPOSE_BUSINESS,
	LOAN_TYPE_MORTGAGE,
	EMP_STAT_SELF_EMPLOYED,
	BOAT_ASSET_TYPE_COMMERCIAL,
	LOAN_PURPOSE_MORTGAGE_REFINANCE,
} from "@/lib/constants";
import {
	PersonalDetails,
	PhoneValidation,
	PropertyDetails,
	AdditionalInfo,
	LivingDetails,
	LoanDetails,
} from "@/components/forms/quickForm/parts/index";
import { BulletSteps } from "@/components/ui";
import { useFormDataStore, useFormStateStore } from "@/components/context/useFormContext";
import { FormHeader } from "@/components/ui";

// For Mortgage Loan Types: Filter form values based on mortgage loan purpose
function getFilteredFormValues(formValues: any, loanType: string, loanPurpose: string) {
	if (loanType === LOAN_TYPE_MORTGAGE && loanPurpose !== LOAN_PURPOSE_MORTGAGE_REFINANCE) {
		// For non-refinance loans, exclude refinance-specific fields
		const {
			existingLender,
			refinancePurpose,
			existingLoanBalance,
			isInFixedRate,
			fixedRateEndDate,
			interestRate,
			...rest
		} = formValues;
		return rest;
	} else if (loanType === LOAN_TYPE_MORTGAGE && loanPurpose === LOAN_PURPOSE_MORTGAGE_REFINANCE) {
		// For refinance loans, exclude purchase-specific fields
		const { propertyDeposit, assetType, purchaseStage, isFirstHomeBuyer, ...rest } = formValues;
		return rest;
	}
	return formValues;
}

function usePrevious<T>(value: T) {
	let ref = useRef(value);

	useEffect(() => {
		ref.current = value;
	}, [value]);

	return ref.current;
}

const variants = {
	initial: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
	animate: {
		opacity: 1,
	},
	exit: (isForwards: boolean) => {
		return {
			opacity: 0,
		};
	},
};

type QuickFormProps = {
	className?: string;
	loanTypes?: string;
	siteTitle?: string;
	rootId?: HTMLElement;
};

export function QuickFormMain({ className, loanTypes, siteTitle, rootId }: QuickFormProps) {
	//Get values and setters from Zustand store
	const { formValues, setFormValue, resetFormValues, setFormError, formErrors } =
		useFormDataStore();
	const {
		firstName,
		lastName,
		email,
		state,
		loanAmount,
		phone,
		loanPurpose,
		loanType,
		assetType,
		purchaseStage,
		assetAge,
		purchaseFrom,
		livingStatus,
		employmentStatus,
		abnStatus,
		gstStatus,
		combinedIncome,
		creditHistory,
	} = formValues;
	// Get form states
	const {
		selectedIndex,
		setSelectedIndex,
		isPhoneVerified,
		setPhoneVerified,
		enabledFields,
		setEnabledFields,
		enabledLoans,
		setEnabledLoans,
		isSubmitted,
		setIsSubmitted,
		validateFormDetails,
		appSettings,
		setAppSettings,
		formContainer,
		setFormContainer,
		toggleTC, //Enables proceeding to the next page if T&A has been agreed.
		setToggleTC,
		stepProcess,
		setStepProcess,
		formError,
		setHasAttemptedSubmit,
	} = useFormStateStore();

	const [stepsMaxIndex, setStepsMaxIndex] = useState(stepProcess.length - 1);

	// Determining the form steps
	const getStepProcess = () => {
		if (loanType === LOAN_TYPE_MORTGAGE) {
			setStepProcess("Mortgage Loan");
			setStepsMaxIndex(stepProcess.length - 1);
		} else {
			setStepProcess("Default");
			setStepsMaxIndex(stepProcess.length - 1);
		}
	};

	// Apply App Setting's Loan Type
	useEffect(() => {
		if (selectedIndex == 0) {
			const loans = loanTypes?.split(",").map((t) => t.trim()) || ["Car Loan"];
			if (loans.length === 1) {
				setFormValue("loanType", loans[0]);
				setEnabledFields(loans[0]); // Sets the allowed fields for a specific loan type.
				getStepProcess();
			}
			setEnabledLoans(loans); // Sets the allowed Loan Type options.
			getStepProcess();
		}
	}, [selectedIndex, setFormValue, loanType, stepProcess, stepsMaxIndex]);

	// Apply Color Theme Settings
	useEffect(() => {
		setAppSettings(siteTitle ?? "Yes Car Loans");

		if (!rootId) return;

		rootId?.style.setProperty(`--yF-theme-color`, appSettings.themeColor);
		rootId?.style.setProperty(`--yF-theme-h-text`, appSettings.themeHeaderTxt);
		rootId?.style.setProperty(`--yF-hover-color`, appSettings.hoverColor);
		rootId?.style.setProperty(`--yF-bullet-bg`, appSettings.bulletBG);
	}, [appSettings, rootId]);

	/* Partner Info */
	const [isPartner, setIsPartner] = useState("No");
	const [partnerName, setPartnerName] = useState("");
	const [partnerId, setPartnerId] = useState("");
	const [partnerEmail, setPartnerEmail] = useState("");
	const [partnerWebsite, setPartnerWebsite] = useState("");
	const [partnerAssetType, setPartnerAssetType] = useState("");
	const hasFetched = useRef(false);

	const detectedSearchEngine = useSearchEngineDetector();
	const [searchEngine, setSearchEngine] = useState<string>("Direct");
	const [isNavDivSmall, setIsNavDivSmall] = useState(false);
	const [tabHasFocus, setTabHasFocus] = useState(true);
	const [browserInfo, setBrowserInfo] = useState({});
	const timer = useRef<NodeJS.Timeout | undefined>(undefined);
	const [userIP, setUserIP] = useState("");
	const [usingVPN, setUsingVPN] = useState(false);
	const prevIndex = usePrevious(selectedIndex);
	const isForwards = prevIndex === undefined ? true : selectedIndex > prevIndex;
	const sourceUrl = window.location.href;

	const loanTypeObject = loan_type.find((type) => type.option === loanType);
	const asset_type_id = loanTypeObject ? loanTypeObject.id : null;
	const residentialTypeObject = living_status.find((type) => type.value === livingStatus);
	const residential_status_id = residentialTypeObject ? residentialTypeObject.id : null;
	const employmentTypeObject = employment_status.find((type) => type.value === employmentStatus);
	const employment_status_id = employmentTypeObject ? employmentTypeObject.id : null;

	const headingRef = useRef<HTMLHeadingElement>(null);
	const navDivRef = useRef<HTMLDivElement | null>(null); //References to the Navigation Button's Div Container

	const isDisabled = (() => {
		if (selectedIndex === 0) {
			return !toggleTC || formError;
		}
		if (selectedIndex === 1) {
			return !isPhoneVerified || formError;
		}
		if (selectedIndex === 2) {
			return formError;
		}
		if (selectedIndex === 3) {
			return formError;
		}
		if (selectedIndex === 4) {
			return formError;
		}
		if (selectedIndex === 5) {
			return formError;
		}
		if (selectedIndex === stepsMaxIndex) {
			return formError;
		}
		return false;
	})();

	// For Navigation Buttons (Previous, Next, Submit)
	useEffect(() => {
		const container = navDivRef.current;
		if (!container) return;

		const observer = new ResizeObserver(([entry]) => {
			let limiter = 600;

			setFormContainer(entry.contentRect.width);
			setIsNavDivSmall(entry.contentRect.width < limiter);
		});

		observer.observe(container);

		return () => observer.disconnect();
	}, [selectedIndex, formContainer]);

	const scrollToHeading = () => {
		if (window.matchMedia("(max-width: 767px)").matches) {
			setTimeout(() => {
				if (headingRef.current) {
					const additionalOffset = 120;
					const targetPosition =
						headingRef.current.getBoundingClientRect().top +
						window.scrollY -
						additionalOffset;

					window.scrollTo({
						top: targetPosition,
						behavior: "smooth",
					});
				}
			}, 100); // 100ms delay to ensure rendering
		}
	};

	const fetchIPData = async () => {
		try {
			const data = await getUserIP();
			setUserIP(data.userIP);
			setUsingVPN(data.usingVPN);
		} catch (error) {
			console.error("Failed to fetch user IP data:", error);
		}
	};

	useEffect(() => {
		setSearchEngine(detectedSearchEngine);
	}, [detectedSearchEngine]);

	// Detect what browser and user IP
	useEffect(() => {
		const browser = Bowser.parse(window.navigator.userAgent);
		const info = `${browser.browser.name} ${browser.browser.version}, ${browser.os.name}, ${browser.platform.type}`;
		setBrowserInfo(info);
	}, []);

	window.leadGenerated = window.leadGenerated || false;

	const loadDataLayerScript = () => {
		if (!window.leadGenerated) {
			window.dataLayer = window.dataLayer || [];
			window.dataLayer.push({
				event: "generate_lead",
				enhanced_conversion_data: {
					email: email,
					phone_number: "+61" + phone?.substring(1),
					first_name: firstName,
					last_name: lastName,
				},
			});
			window.leadGenerated = true;
		}
	};

	const IP_STORAGE_KEY = "processedIPs";
	const EXPIRATION_TIME = 30 * 60 * 1000;

	const getProcessedIPs = (): { userIP: string; timestamp: number }[] => {
		const ips = localStorage.getItem(IP_STORAGE_KEY);
		return ips ? JSON.parse(ips) : [];
	};

	const addProcessedIP = (userIP: string) => {
		const ips = getProcessedIPs();
		const now = Date.now();
		const updatedIPs = ips.filter(({ timestamp }) => now - timestamp < EXPIRATION_TIME);
		updatedIPs.push({ userIP, timestamp: now });
		localStorage.setItem(IP_STORAGE_KEY, JSON.stringify(updatedIPs));
	};

	const isIPProcessed = (userIP: string): boolean => {
		const ips = getProcessedIPs();
		const now = Date.now();
		return ips.some(
			({ userIP: storedIP, timestamp }) =>
				storedIP === userIP && now - timestamp < EXPIRATION_TIME,
		);
	};

	const sendPreZap = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const filteredFormValues =
				loanType !== LOAN_TYPE_MORTGAGE
					? formValues
					: getFilteredFormValues(formValues, loanType, loanPurpose);

			const zapPost = {
				...filteredFormValues,
				dateSubmitted: getCurrentTime(),
				application_type_id:
					loanPurpose === LOAN_PURPOSE_PERSONAL || asset_type_id === 5 ? 1 : 2,
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Partial Submission`,
				firstName: firstName,
				lastName: lastName,
				email: email,
				phone: phone,
				state: state,
				loanType: loanType,
				loanAmount: loanAmount,
				loanPurpose: loanPurpose,
				assetType: assetType,
				asset_type_id: asset_type_id,
				assetAge: assetAge,
				purchaseStage: purchaseStage,
				purchaseFrom: purchaseFrom,
				livingStatus: livingStatus,
				residential_status_id: residential_status_id,
				employmentStatus:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? EMP_STAT_SELF_EMPLOYED
						: employmentStatus,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				abnStatus: abnStatus,
				gstStatus: gstStatus,
				combinedIncome: combinedIncome,
				creditHistory: creditHistory,
				isPartial: "Yes",
				source_id: 18,
				source: sourceUrl,
				isUsingVPN: usingVPN.toString(),
				userIP: userIP,
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) => value !== null && value !== undefined && value !== "",
				),
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendPreZapMail = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const filteredFormValues =
				loanType !== LOAN_TYPE_MORTGAGE
					? formValues
					: getFilteredFormValues(formValues, loanType, loanPurpose);

			const zapPost = {
				...filteredFormValues,
				dateSubmitted: getCurrentTime(),
				application_type_id:
					loanPurpose === LOAN_PURPOSE_PERSONAL || asset_type_id === 5 ? 1 : 2,
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Partial Submission`,
				firstName: firstName,
				lastName: lastName,
				email: email,
				phone: phone,
				state: state,
				loanType: loanType,
				loanAmount: loanAmount,
				loanPurpose: loanPurpose,
				assetType: assetType,
				asset_type_id: asset_type_id,
				assetAge: assetAge,
				purchaseStage: purchaseStage,
				purchaseFrom: purchaseFrom,
				livingStatus: livingStatus,
				residential_status_id: residential_status_id,
				employmentStatus:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? EMP_STAT_SELF_EMPLOYED
						: employmentStatus,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				abnStatus: abnStatus,
				gstStatus: gstStatus,
				combinedIncome: combinedIncome,
				creditHistory: creditHistory,
				isPartial: "Yes",
				source_id: 18,
				source: sourceUrl,
				isUsingVPN: usingVPN.toString(),
				userIP: userIP,
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) => value !== null && value !== undefined && value !== "",
				),
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {},
	});

	const sendZap = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const filteredFormValues =
				loanType !== LOAN_TYPE_MORTGAGE
					? formValues
					: getFilteredFormValues(formValues, loanType, loanPurpose);

			const zapPost = {
				...filteredFormValues,
				dateSubmitted: getCurrentTime(),
				application_type_id:
					loanPurpose === LOAN_PURPOSE_PERSONAL || asset_type_id === 5 ? 1 : 2,
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Quick Quote`,
				loanAmount: loanAmount,
				combinedIncome: combinedIncome,
				asset_type_id: asset_type_id,
				residential_status_id: residential_status_id,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) => value !== null && value !== undefined && value !== "",
				),
			);

			const data = await postZap(cleanedZapPost);
			return data;
		},
		onSuccess() {
			resetFormValues();
			setToggleTC(false);
			redirectToThankYou();
		},
	});

	const sendMail = useMutation({
		mutationKey: [formValues],
		mutationFn: async () => {
			const filteredFormValues =
				loanType !== LOAN_TYPE_MORTGAGE
					? formValues
					: getFilteredFormValues(formValues, loanType, loanPurpose);

			const zapPost = {
				...filteredFormValues,
				dateSubmitted: getCurrentTime(),
				application_type_id:
					loanPurpose === LOAN_PURPOSE_PERSONAL || asset_type_id === 5 ? 1 : 2,
				siteTitle: `${siteTitle}`,
				formName: `${siteTitle} Quick Quote`,
				loanAmount: loanAmount,
				combinedIncome: combinedIncome,
				asset_type_id: asset_type_id,
				residential_status_id: residential_status_id,
				employment_status_id:
					loanType === LOAN_TYPE_TRUCK ||
					loanType === LOAN_TYPE_EQUIPMENT ||
					assetType === BOAT_ASSET_TYPE_COMMERCIAL ||
					loanPurpose === LOAN_PURPOSE_BUSINESS
						? 4
						: employment_status_id,
				source: sourceUrl,
				source_id: 18,
				userIP: userIP,
				isUsingVPN: usingVPN.toString(),
				userAgent: browserInfo,
				searchEngine: searchEngine,
				isReferrer: isPartner,
				partnerName: partnerName,
				partnerEmail: partnerEmail,
				referralId: partnerId,
				partnerWebsite: partnerWebsite,
			};

			const cleanedZapPost = Object.fromEntries(
				Object.entries(zapPost).filter(
					([_, value]) => value !== null && value !== undefined && value !== "",
				),
			);

			const data = await sendEmail(cleanedZapPost);
			return data;
		},
		onSuccess() {
			setToggleTC(false);
		},
	});

	// Prevents Next button from being spammed causing skipping multiple pages.
	const [isPageLoading, setIsPageLoading] = useState(false);

	const next = useCallback(() => {
		if (isPageLoading) return;
		setHasAttemptedSubmit(true);

		let indexesWithValidation =
			loanType === LOAN_TYPE_MORTGAGE ? [0, 2, 3, 4, 5] : [0, 2, 3, 4];

		if (indexesWithValidation.includes(selectedIndex)) {
			let validate = validateFormDetails;
			const isValid = validate?.();
			if (!isValid) return; // Stops next page if form errors exist.
		}

		if (selectedIndex < stepsMaxIndex) {
			setIsPageLoading(true);
			scrollToHeading();
			setSelectedIndex(selectedIndex + 1);
			setTimeout(() => setIsPageLoading(false), 500); // Add delay to give time for components to load.
		}
	}, [
		isPageLoading,
		selectedIndex,
		stepsMaxIndex,
		scrollToHeading,
		isPhoneVerified,
		formErrors,
		formError,
		setFormError,
		setHasAttemptedSubmit,
	]);

	function prev() {
		if (selectedIndex === 1) {
			scrollToHeading();
			setSelectedIndex(selectedIndex - 1);
			// For enabled multiple loan types, reset Loan Purpose and Asset Type in case loan type changes.
			if (enabledLoans.length > 1) {
				setFormValue("loanPurpose", "");
				setFormValue("assetType", "");
			}
		} else if (selectedIndex > 0 && selectedIndex < stepsMaxIndex) {
			scrollToHeading();
			setSelectedIndex(selectedIndex - 1);
			if (
				// 86cyywnev - Single Loan Purpose Option does not need ABN Status cleared.
				enabledFields.loanPurpose?.length != 1 &&
				enabledFields.loanPurpose[0].value != LOAN_PURPOSE_BUSINESS
			) {
				setFormValue("abnStatus", "");
				setFormValue("gstStatus", "");
			}
		}
	}

	const close = useCallback(() => {
		setIsSubmitted(false);
		setPhoneVerified(false);
		resetFormValues();
		setSelectedIndex(0);
		setToggleTC(false);

		const $ = (window as any).jQuery;
		if ($) {
			const bodyClass = document.body.className;
			if (bodyClass.includes("wp-theme-yestheme")) {
				$("#customModalBackdrop").addClass("hidden");
				$("body").removeClass("no-scroll");
				$(".quick-quote-loan-amount").val(30000);
			}
		}
	}, [siteTitle, selectedIndex, stepsMaxIndex]);

	const redirectToThankYou = useCallback(() => {
		if (loanType !== LOAN_TYPE_MORTGAGE) {
			const siteUrl = window.location.origin;
			window.location.href = `${siteUrl}/thank-you`;
		}
	}, [loanType]);

	// Handle External Modal Close Button (XCircle)
	useEffect(() => {
		const handleModalClose = () => {
			// Only reset when on the success page
			if (selectedIndex === stepsMaxIndex) {
				close();
			}
		};

		// Find the external close button
		const closeButton = document.querySelector("#modalCloseBtn, #customModalClose");
		if (closeButton) {
			closeButton.addEventListener("click", handleModalClose);
		}

		// Cleanup On Unmount
		return () => {
			if (closeButton) {
				closeButton.removeEventListener("click", handleModalClose);
			}
		};
	}, [selectedIndex, stepsMaxIndex]);

	// Detect if tab is inactive
	useEffect(() => {
		const handleFocus = () => {
			setTabHasFocus(true);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};

		const handleBlur = () => {
			setTabHasFocus(false);
			timer.current = setTimeout(() => {
				// Handle the tab being inactive for 3 minutes here
				if (isPhoneVerified && !isSubmitted && !isIPProcessed(userIP)) {
					sendPreZap.mutate();
					sendPreZapMail.mutate();
					addProcessedIP(userIP);
				}
			}, 180000); // 180000 milliseconds = 3 minutes
		};

		window.addEventListener("focus", handleFocus);
		window.addEventListener("blur", handleBlur);

		// Clean up
		return () => {
			window.removeEventListener("focus", handleFocus);
			window.removeEventListener("blur", handleBlur);
			if (timer.current !== undefined) {
				clearTimeout(timer.current);
			}
		};
	}, [isPhoneVerified, isSubmitted, userIP]);

	// Detect when tab is reloaded and closed
	useEffect(() => {
		if (selectedIndex < stepsMaxIndex) {
			const handleBeforeUnload = (event: any) => {
				if (isPhoneVerified && !isSubmitted && !isIPProcessed(userIP)) {
					sendPreZap.mutate();
					sendPreZapMail.mutate();
					addProcessedIP(userIP);
				}
				setPhoneVerified(false);
				setIsSubmitted(false);
				setSelectedIndex(0);
				resetFormValues();
			};

			window.addEventListener("beforeunload", handleBeforeUnload);
			window.addEventListener("pagehide", handleBeforeUnload);

			return () => {
				window.removeEventListener("beforeunload", handleBeforeUnload);
				window.removeEventListener("pagehide", handleBeforeUnload);
			};
		}
	}, [isPhoneVerified, isSubmitted, selectedIndex, userIP, stepsMaxIndex]);

	// Last Page, trigger Zap and Mail submission
	useEffect(() => {
		if (selectedIndex === stepsMaxIndex && isPhoneVerified && !isSubmitted) {
			sendMail.mutate();
			sendZap.mutate();
			setIsSubmitted(true);
			loadDataLayerScript();
		}
	}, [isPhoneVerified, selectedIndex, isSubmitted, stepsMaxIndex]);

	// Go to next page when phone is verified
	useEffect(() => {
		if (isPhoneVerified) {
			next();
			fetchIPData();
		}
	}, [isPhoneVerified]);

	const handleKeyDown = useCallback((event: any) => {
		if (event.key === "Enter") {
			event.preventDefault();
		}
	}, []);

	// JQuery for getting the loan amount value field in the homepage and why jade page
	// Jade Truck have different page id for why jade page
	// Jade Finance and Jade Boat Loans why jade do not have the input field
	useEffect(() => {
		const $ = (window as any).jQuery;

		if ($) {
			const bodyClass = document.body.classList;

			const isSingleCalculatorTemplate = [
				"wp-theme-yestheme", // Yes Theme
			];
			const isSingleCalculator = isSingleCalculatorTemplate.some((className) =>
				bodyClass.contains(className),
			);

			if (isSingleCalculator) {
				const updateLoanAmount = (elementID: string) => {
					const inputValue = isSingleCalculator ? ($(elementID).val() as string) : "";

					if (inputValue) {
						let formattedValue = +inputValue.replace(/[^0-9]/g, "");
						setFormValue("loanAmount", formattedValue.toLocaleString());
					} else {
						console.warn("Loan Amount not retrieved using ", elementID);
					}
				};

				// Trigger Update Loan Amount on CTA click
				if (isSingleCalculator) {
					// Other Jade Sites
					$(document).on(
						"click",
						"#mgCalc-trigger-cta, #mgCalc-trigger-cta-mobile",
						function () {
							updateLoanAmount("#mgCalc-loan-amount");
						},
					);
					$(document).on(
						"click",
						"#jFcalc-trigger-cta, #jFcalc-trigger-cta-mobile",
						function () {
							updateLoanAmount("#jFcalc-loan-amount");
						},
					);
				}
			}

			return () => {
				if (isSingleCalculator) {
					$(document).off("click", "#mgCalc-trigger-cta");
					$(document).off("click", "#mgCalc-trigger-cta-mobile");
					$(document).off("click", "#jFcalc-trigger-cta");
					$(document).off("click", "#jFcalc-trigger-cta-mobile");
				}
			};
		}
	}, [setFormValue]);

	return (
		<div ref={navDivRef} className={clsx(className, "yF-w-full yF-relative")}>
			{/* Form */}
			<AnimateChangeInHeight>
				<form
					id={rootId?.id + "Form"}
					className="yF-pb-6 yF-pt-6 yF-px-5"
					onKeyDown={handleKeyDown}
					onSubmit={(e) => e.preventDefault()}
				>
					{/* Loading Overlay */}
					{sendZap.isPending && (
						<motion.div
							initial={{ opacity: 0 }}
							animate={{ opacity: 1 }}
							exit={{ opacity: 0 }}
							transition={{ duration: 0.2 }}
							className="yF-absolute yF-w-full yF-h-full yF-top-0 yF-left-0 yF-bg-slate-200 yF-bg-opacity-75 yF-z-50"
						>
							<div className="yF-flex yF-justify-center yF-items-center yF-h-full yF-w-full">
								<div role="status">
									<svg
										aria-hidden="true"
										className="yF-w-12 yF-h-12 yF-text-opacity-0 yF-text-white yF-animate-spin yF-bg-opacity-0 yF-fill-[--yF-theme-color]"
										viewBox="0 0 100 101"
										fill="none"
										xmlns="http://www.w3.org/2000/svg"
									>
										<path
											d="M100 50.5908C100 78.2051 77.6142 100.591 50 100.591C22.3858 100.591 0 78.2051 0 50.5908C0 22.9766 22.3858 0.59082 50 0.59082C77.6142 0.59082 100 22.9766 100 50.5908ZM9.08144 50.5908C9.08144 73.1895 27.4013 91.5094 50 91.5094C72.5987 91.5094 90.9186 73.1895 90.9186 50.5908C90.9186 27.9921 72.5987 9.67226 50 9.67226C27.4013 9.67226 9.08144 27.9921 9.08144 50.5908Z"
											fill="currentColor"
										/>
										<path
											d="M93.9676 39.0409C96.393 38.4038 97.8624 35.9116 97.0079 33.5539C95.2932 28.8227 92.871 24.3692 89.8167 20.348C85.8452 15.1192 80.8826 10.7238 75.2124 7.41289C69.5422 4.10194 63.2754 1.94025 56.7698 1.05124C51.7666 0.367541 46.6976 0.446843 41.7345 1.27873C39.2613 1.69328 37.813 4.19778 38.4501 6.62326C39.0873 9.04874 41.5694 10.4717 44.0505 10.1071C47.8511 9.54855 51.7191 9.52689 55.5402 10.0491C60.8642 10.7766 65.9928 12.5457 70.6331 15.2552C75.2735 17.9648 79.3347 21.5619 82.5849 25.841C84.9175 28.9121 86.7997 32.2913 88.1811 35.8758C89.083 38.2158 91.5421 39.6781 93.9676 39.0409Z"
											fill="currentFill"
										/>
									</svg>
									<span className="yF-sr-only">Loading...</span>
								</div>
							</div>
						</motion.div>
					)}

					{/* Form Pages */}
					<AnimatePresence initial={false} mode="wait" custom={isForwards}>
						{selectedIndex === 0 && (
							<motion.div
								key={0}
								initial="initial"
								animate="animate"
								exit="exit"
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								variants={variants}
								custom={isForwards}
								className="yF-space-y-4"
							>
								<FormHeading headingRef={headingRef} loanType={loanType} />
								<PersonalDetails loanType={loanType} />
							</motion.div>
						)}

						{selectedIndex === 1 && (
							<motion.div
								key={1}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
							>
								{!isPhoneVerified ? (
									<>
										<OTPHeading headingRef={headingRef} />
										<PhoneValidation />
									</>
								) : (
									<>
										<FormVerified headingRef={headingRef} />
									</>
								)}
							</motion.div>
						)}

						{selectedIndex === 2 && (
							<motion.div
								key={2}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="yF-space-y-4"
							>
								<LoanDetails />
							</motion.div>
						)}

						{selectedIndex === 3 && (
							<motion.div
								key={3}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="yF-space-y-4"
							>
								{loanType == LOAN_TYPE_MORTGAGE ? (
									<PropertyDetails />
								) : (
									<LivingDetails />
								)}
							</motion.div>
						)}

						{selectedIndex === 4 && (
							<motion.div
								key={4}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="yF-space-y-4"
							>
								{loanType == LOAN_TYPE_MORTGAGE ? (
									<LivingDetails />
								) : (
									<AdditionalInfo />
								)}
							</motion.div>
						)}

						{selectedIndex === 5 && loanType == LOAN_TYPE_MORTGAGE && (
							<motion.div
								key={5}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="yF-space-y-4"
							>
								<AdditionalInfo />
							</motion.div>
						)}

						{/* Success Page */}
						{selectedIndex === stepsMaxIndex && (
							<motion.div
								key={selectedIndex}
								initial="initial"
								animate="animate"
								exit="exit"
								variants={variants}
								transition={{
									duration: 0.3,
									ease: "easeInOut",
								}}
								custom={isForwards}
								className="space-y-4"
							>
								<FormSuccessHeading headingRef={headingRef} />
							</motion.div>
						)}
					</AnimatePresence>
				</form>
			</AnimateChangeInHeight>

			{/* Navigation */}
			{selectedIndex < stepsMaxIndex ? (
				<div
					className={clsx(
						isNavDivSmall && selectedIndex == 0 ? "yF-flex-nowrap" : "",
						isNavDivSmall ? "yF-flex-wrap" : "yF-flex-nowrap",
						"yF-flex yF-items-center yF-justify-center yF-border-t yF-border-gray-200 yF-bg-white yF-py-3 yF-px-4",
					)}
				>
					<BulletSteps
						steps={stepProcess}
						selectedIndex={selectedIndex}
						navRef={isNavDivSmall}
					/>
					<div
						className={clsx(
							selectedIndex === stepsMaxIndex
								? "yF-hidden"
								: "yF-flex yF-flex-1 yF-justify-end",
						)}
					>
						<button
							disabled={selectedIndex === 1 && !isPhoneVerified}
							className={clsx(
								selectedIndex === 0 || (selectedIndex === 1 && !isPhoneVerified)
									? "yF-hidden"
									: isNavDivSmall
										? "yF-w-full"
										: "yF-w-auto",
								"yF-bg-white hover:yF-bg-gray-50 yF-relative yF-inline-flex yF-items-center yF-rounded-md yF-justify-center yF-px-3 yF-py-2 yF-text-lg yF-font-semibold yF-text-gray-900 yF-ring-1 yF-ring-inset yF-ring-gray-300 focus-visible:yF-outline-offset-0",
							)}
							onClick={() => prev()}
						>
							Previous
						</button>
						<button
							className={clsx(
								isDisabled
									? "yF-bg-gray-100 yF-text-gray-400 yF-ring-gray-100"
									: "yF-bg-[--yF-theme-color] hover:yF-bg-[--yF-hover-color]",
								isNavDivSmall
									? "yF-w-full yF-text-white"
									: "yF-w-auto yF-text-white",
								"yF-relative yF-ml-3 yF-inline-flex yF-items-center yF-rounded-md yF-justify-center yF-px-3 yF-py-2 yF-text-lg yF-font-semibold yF-ring-1 yF-ring-inset yF-ring-[--yF-theme-color] focus-visible:yF-outline-offset-0",
							)}
							onClick={() => {
								if (selectedIndex !== 1 || isPhoneVerified) {
									next();
								}
							}}
						>
							{selectedIndex === stepsMaxIndex - 1 ? "Submit" : "Next"}
						</button>
					</div>
				</div>
			) : (
				<div className="yF-flex yF-items-center yF-flex-wrap yF-justify-center yF-border-t yF-border-gray-200 yF-bg-white yF-mt-2 yF-px-4 yF-py-3 sm:yF-px-6 sm:yF-justify-between">
					<button
						className="yF-bg-[--yF-theme-color] hover:yF-bg-[--yF-hover-color] yF-relative yF-mb-2 yF-mx-auto yF-block yF-w-1/3 yF-text-center yF-rounded-md yF-px-3 yF-py-2 text-md yF-font-semibold yF-text-white yF-ring-1 yF-ring-inset yF-ring-[--yF-theme-color] focus-visible:yF-outline-offset-0"
						onClick={() => {
							close();
						}}
					>
						Close
					</button>
				</div>
			)}
		</div>
	);
}

function OTPHeading({
	className,
	headingRef,
}: {
	className?: string;
	headingRef: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "yF-relative")}>
				<div className="yF-mx-auto yF-flex yF-h-20 yF-w-20 yF-items-center yF-justify-center yF-rounded-full yF-bg-[--yF-bullet-bg]">
					<DevicePhoneMobileIcon
						className="yF-h-10 yF-w-10 yF-text-[--yF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="yF-mt-3 yF-text-center sm:yF-mt-5">
					<h3
						ref={headingRef}
						className="yF-text-xl yF-font-semibold yF-leading-8 yF-text-gray-900"
					>
						Verify Your Number
					</h3>
				</div>
			</div>
		</>
	);
}

function FormHeading({
	className,
	headingRef,
	loanType,
}: {
	className?: string;
	headingRef: RefObject<HTMLHeadingElement | null>;
	loanType: string;
}) {
	const isMortgage = loanType === LOAN_TYPE_MORTGAGE;

	return (
		<div className={clsx(className, "yF-relative")}>
			<FormHeader
				formType="quickForm"
				title={isMortgage ? "Compare Rates with an Expert" : "Get a Quick Quote"}
				headingRef={headingRef}
			/>
		</div>
	);
}

function FormSuccessHeading({
	className,
	headingRef,
}: {
	className?: string;
	headingRef: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "yF-relative")}>
				<div className="yF-mx-auto yF-flex yF-h-20 yF-w-20 yF-items-center yF-justify-center yF-rounded-full yF-bg-[--yF-bullet-bg]">
					<CheckIcon
						className="yF-h-10 yF-w-10 yF-text-[--yF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="yF-mt-3 yF-text-center sm:yF-mt-5">
					<h3
						ref={headingRef}
						className="yF-text-[2rem] yF-mb-3 yF-font-semibold yF-leading-8 yF-text-[--yF-theme-h-text]"
					>
						Successfully Submitted
					</h3>
					<div className="yF-mt-5 yF-mb-5">
						<p className="yF-text-md yF-text-gray-500">
							Thanks for contacting us! We will get in touch with you shortly.
						</p>
					</div>
				</div>
			</div>
		</>
	);
}

function FormVerified({
	className,
	headingRef,
}: {
	className?: string;
	headingRef: RefObject<HTMLHeadingElement | null>;
}) {
	return (
		<>
			<div className={clsx(className, "yF-relative")}>
				<div className="yF-mx-auto yF-flex yF-h-20 yF-w-20 yF-items-center yF-justify-center yF-rounded-full yF-bg-[--yF-bullet-bg]">
					<CheckIcon
						className="yF-h-10 yF-w-10 yF-text-[--yF-theme-color]"
						aria-hidden="true"
					/>
				</div>
				<div className="yF-mt-3 yF-text-center sm:yF-mt-5">
					<h3
						ref={headingRef}
						className="yF-text-[2rem] yF-mb-3 yF-font-semibold yF-leading-8 yF-text-[--yF-theme-h-text]"
					>
						Thank you!
					</h3>
					<div className="yF-mt-5 yF-mb-5">
						<p className="yF-text-md yF-text-gray-500">Successfully verified</p>
					</div>
				</div>
			</div>
		</>
	);
}
